import React, { useState } from 'react';
import { ShoppingItem } from '../types';
import { Check, Square, Trash2, Edit2, Save, X, GripVertical } from 'lucide-react';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface ItemCardProps {
  item: ShoppingItem;
  onToggle: (itemId: string, checked: boolean) => void;
  onDelete?: (itemId: string) => void;
  onUpdate?: (itemId: string, name: string, quantity: number) => void;
  canEdit?: boolean;
}

const ItemCard: React.FC<ItemCardProps> = ({ item, onToggle, onDelete, onUpdate, canEdit = true }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(item.name);
  const [editQuantity, setEditQuantity] = useState(item.quantity);
  const [editQuantityInput, setEditQuantityInput] = useState(item.quantity.toString());

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditName(item.name);
    setEditQuantity(item.quantity);
    setEditQuantityInput(item.quantity.toString());
  };

  const handleSave = () => {
    if (onUpdate && editName.trim()) {
      onUpdate(item.id, editName.trim(), editQuantity);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditName(item.name);
    setEditQuantity(item.quantity);
    setEditQuantityInput(item.quantity.toString());
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg border p-4 transition-all duration-200 ${
        item.checked 
          ? 'border-green-200 bg-green-50 opacity-75' 
          : 'border-gray-200 hover:border-blue-300'
      }`}
    >
      <div className="flex items-start space-x-3">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="mt-1 p-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
        >
          <GripVertical className="w-4 h-4" />
        </div>
        
        <button
          onClick={() => canEdit && onToggle(item.id, !item.checked)}
          disabled={!canEdit}
          className={`mt-1 p-1 rounded transition-colors ${
            canEdit ? 'hover:bg-gray-100' : 'cursor-not-allowed'
          }`}
        >
          {item.checked ? (
            <Check className="w-5 h-5 text-green-600" />
          ) : (
            <Square className="w-5 h-5 text-gray-400" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Item name"
                  autoFocus
                />
                <input
                  type="number"
                  value={editQuantityInput}
                  onChange={(e) => setEditQuantityInput(e.target.value)}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value === '' || isNaN(parseInt(value)) || parseInt(value) < 1) {
                      setEditQuantityInput('1');
                      setEditQuantity(1);
                    } else {
                      const numValue = parseInt(value);
                      setEditQuantityInput(numValue.toString());
                      setEditQuantity(numValue);
                    }
                  }}
                  onKeyDown={handleKeyPress}
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="1"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={handleSave}
                    disabled={!editName.trim()}
                    className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save</span>
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center space-x-1 px-3 py-1 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h4 className={`font-medium ${
                  item.checked ? 'text-green-700 line-through' : 'text-gray-900'
                }`}>
                  {item.name}
                </h4>
                <div className="flex items-center space-x-1">
                  {onUpdate && canEdit && !item.checked && (
                    <button
                      onClick={handleEdit}
                      className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                      title="Edit item"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                  {onDelete && canEdit && (
                    <button
                      onClick={() => onDelete(item.id)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-2">
                <span className={`text-sm ${
                  item.checked ? 'text-green-600' : 'text-gray-500'
                }`}>
                  Quantity: {item.quantity}
                </span>
                
                {item.checked && item.checked_at && (
                  <span className="text-xs text-green-600">
                    Completed {formatDate(item.checked_at)}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemCard;
